import type { ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { moduleOf, shellQuery } from '../api/shell'
import { usePageStyles } from '../ui/usePageStyles'
import './BoardLook.css'

/**
 * DESENUL tablourilor de bord, fara MASINARIA lor.
 * =============================================================================
 *
 * Cele cinci tablouri noi (`/app/dashboard`, si cele de modul) sunt un perete
 * de carduri desenate dintr-un CATALOG scris de mana: fiecare card e o cifra cu
 * o tinta, doua praguri, un drept si o linie de istorie pe zile - vezi
 * `application/Services/Dashboard/Catalog.php` si `card.php`.
 *
 * Ecranele vechi care se muta pe desenul asta NU incap in catalogul ala, si nici
 * n-ar trebui sa incapa:
 *
 *   - Core Applications Dashboard arata cate o dala pentru FIECARE aplicatie pe
 *     care si-a facut-o clientul. Lista difera de la o firma la alta si creste
 *     cand omul adauga una. Un catalog scris de mana nu poate avea un rand pe
 *     care nu-l stie nimeni dinainte;
 *   - cifrele lor nu au istorie pe zile, deci n-ar avea ce desena sub ele;
 *   - dreptul se cere pe ECRAN, o data, nu pe fiecare cifra in parte.
 *
 * Deci se ia DESENUL si se lasa masinaria: cutia cardului, grila si marimea
 * celulei, spatiile, tipografia cifrei si a titlului, culorile de stare,
 * placuta modulului si taierea cardului la marginea celulei lui. Toate vin din
 * `od_dashboard.css`, foaia adevarata a tablourilor: nu se copiaza nicio regula
 * si nu se scrie niciun hexa. Daca maine se schimba rotunjimea cardurilor, se
 * schimba si aici, singura.
 *
 * -----------------------------------------------------------------------------
 * DE CE E SCRIS AICI, SI NU IN ECRANUL CARE L-A CERUT
 * -----------------------------------------------------------------------------
 *
 * Pe 11 septembrie 2026 se mutau pe desenul asta trei ecrane deodata, de trei
 * agenti diferiti: Core Applications, SNMP Dashboard si Endpoint Performance.
 * Toate trei au aceeasi forma - un rand de contoare sus, un perete de dale
 * dedesubt - si fara un loc comun ar fi iesit trei ajutoare aproape la fel,
 * care s-ar fi departat unul de altul la prima schimbare. Exact asa s-au facut
 * cele 58 de copii ale lui `dashboard-stat` pe care desenul asta le inlocuieste.
 *
 * -----------------------------------------------------------------------------
 * REGULA CARE A COSTAT O ZI, SI CARE E RESPECTATA AICI
 * -----------------------------------------------------------------------------
 *
 * `.odb-card` E COPILUL DIRECT AL LUI `.odb-slot`. Fara niciun invelis.
 *
 * Grila are randuri de inaltime FIXA (`grid-auto-rows: var(--odb-cell)`), si
 * tot ce tine cardul in celula lui se scrie in `od_dashboard.css` pe copilul
 * direct:
 *
 *     .odb-slot > .odb-card { min-height: 0; overflow: hidden; flex: 1 1 auto; }
 *
 * Cu un `<div>` la mijloc selectorul nu se potriveste, cardul nu se mai taie la
 * marginea celulei si se aseaza PESTE vecinul de sub el. S-a intamplat pe 10
 * septembrie 2026, pe toate cele cinci tablouri si pe wallboard deodata, dintr-un
 * singur element in plus - vezi `DashboardCard.tsx`. De-aia `BoardCard` scrie
 * cele doua elemente el insusi si nu primeste nimic intre ele.
 */

/**
 * Pagina: fundalul, marginile si scara tablourilor, plus foaia lor.
 *
 * Foaia se cere la deschidere si se scoate la inchidere (`usePageStyles`), ca
 * regulile ei sa nu ajunga pe alte ecrane - la fel ca pe tablourile adevarate.
 */
export function BoardLook({ children }: { children: ReactNode }) {
  const path = typeof window === 'undefined' ? '' : window.location.pathname
  const shell = useQuery(shellQuery(moduleOf(path)))
  usePageStyles(['css/od_dashboard.css'], shell.data?.version ?? '')

  return <div className="odb-page">{children}</div>
}

/**
 * Banda de contoare de deasupra grilei - cate incap pe rand.
 *
 * Vezi `BoardLook.css` pentru de ce nu sunt carduri de celula intreaga.
 */
export function BoardStrip({ children }: { children: ReactNode }) {
  return <div className="odb-strip">{children}</div>
}

/**
 * Grila tablourilor: patru coloane, randuri de `--odb-cell`, golurile umplute.
 *
 * `id` se poate schimba fiindca un ecran poate avea DOUA grile - vezi
 * `BoardPart`. `odbGrid` e numarul pe care il poarta grila pe toate tablourile,
 * si ramane cel implicit; a doua grila de pe acelasi ecran isi ia altul, ca sa
 * nu existe doua elemente cu acelasi numar in pagina. Desenul vine din clasa
 * `.odb`, nu din numar.
 */
export function BoardGrid({ id = 'odbGrid', children }: { id?: string; children: ReactNode }) {
  return (
    <div className="odb" id={id}>
      {children}
    </div>
  )
}

/**
 * O PARTE a tabloului, cu numele ei deasupra.
 *
 * Un tablou cu o singura grila n-are nevoie de asa ceva - titlul ecranului
 * spune deja despre ce e vorba. Cand pe acelasi ecran stau insa doua seturi de
 * carduri care raspund la aceeasi intrebare pentru DOUA lucruri diferite -
 * Productivitee are, de pe 21 septembrie 2026, aceleasi trei grafice pentru
 * masini si pentru oameni - fara un nume deasupra fiecaruia omul vede sase
 * desene la rand si trebuie sa citeasca fiecare titlu ca sa-si dea seama unde
 * s-a trecut de la unele la altele.
 *
 * E un `<section>` cu titlul lui, nu un `<div>` cu text ingrosat: cine citeste
 * ecranul cu un cititor de ecran sare de la o parte la alta, si ajunge acolo
 * dintr-o apasare.
 *
 * Numele e mic si majuscul, nu inca un titlu mare: deasupra lui sta deja
 * numele ecranului, iar doua titluri de 26px unul sub altul s-ar bate cap in
 * cap. Grila din el ramane `BoardGrid`, neschimbata.
 */
export function BoardPart({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="odb-part" aria-label={title}>
      <h2 className="odb-part-name">{title}</h2>
      {children}
    </section>
  )
}

/** Starea unei cifre, in limbajul tablourilor. `flat` = fara culoare de stare. */
export type BoardTone = 'ok' | 'warn' | 'crit' | 'flat' | 'unknown'

type CardProps = {
  /** Modulul, pentru culoarea placutei: `proactivitee`, `inventoree`, ... */
  module?: string
  tone?: BoardTone
  /** Numele iconitei, fara fata: `fa-database`. */
  icon?: string
  /** Fata iconitei. `fas` (plina) e cea a tuturor cardurilor din catalog. */
  iconFace?: string
  /** Cifra. Lipsa ei inseamna un card fara raspuns, nu un zero. */
  value?: ReactNode
  /** Ce sta lipit de cifra: `%`, `d`, sau o iconita mica. */
  unit?: ReactNode
  /** Randul mic si majuscul de deasupra titlului (pe carduri: numele modulului). */
  kicker?: string
  title: string
  /** Doua coloane / doua randuri din grila, ca `odb-x2` / `odb-y2` din catalog. */
  wide?: boolean
  tall?: boolean
  /** Continutul de sub cap. */
  children?: ReactNode
  /** Randul lipit de fundul cardului. */
  foot?: ReactNode
  /**
   * Id-ul locului din grila, cand alt ecran duce aici cu o ancora (`#app-1` pe
   * harta aplicatiilor). Sta pe `.odb-slot`, nu pe card: cardul ramane copilul
   * lui direct, fara niciun invelis.
   */
  id?: string
}

/**
 * Un card, in celula lui.
 *
 * Scrie exact markupul lui `card.php` / `CardMarkup`: cifra, placuta cu
 * iconita, numele modulului si titlul, toate pe randul capului. Nu scrie
 * istorie, praguri sau tinta - alea sunt ale catalogului, si ecranele astea
 * n-au de unde sa le ia.
 */
export function BoardCard({
  module,
  tone = 'flat',
  icon,
  iconFace = 'fas',
  value,
  unit,
  kicker,
  title,
  wide,
  tall,
  children,
  foot,
  id,
}: CardProps) {
  const slot = `odb-slot${wide ? ' odb-x2' : ''}${tall ? ' odb-y2' : ''}`
  const card = `odb-card${module ? ` odb-m-${module}` : ''} odb-t-${tone}`

  return (
    <div className={slot} id={id}>
      {/* `.odb-card` copil DIRECT - vezi capul fisierului. */}
      <div className={card}>
        <div className="odb-head">
          {value !== undefined && (
            <span className="odb-figure">
              <span className="odb-value">
                {value}
                {unit !== undefined && <span className="odb-unit">{unit}</span>}
              </span>
            </span>
          )}
          {icon && (
            <span className="odb-tile">
              <i className={`${iconFace} ${icon}`} aria-hidden="true" />
            </span>
          )}
          <span className="odb-names">
            {kicker && <span className="odb-mod">{kicker}</span>}
            <span className="odb-title">{title}</span>
          </span>
        </div>
        {children}
        {foot && <div className="odb-foot">{foot}</div>}
      </div>
    </div>
  )
}

/**
 * Un contor din banda de sus: aceeasi cutie si aceeasi tipografie, un rand.
 *
 * Nu sta in grila, deci n-are `.odb-slot` - si atunci nici regula de taiere nu-l
 * priveste: inaltimea lui e cat ii trebuie.
 */
export function BoardStat({
  module,
  tone = 'flat',
  icon,
  iconFace = 'fas',
  value,
  unit,
  kicker,
  title,
}: Omit<CardProps, 'children' | 'foot' | 'wide' | 'tall' | 'id'>) {
  return (
    <div className={`odb-card${module ? ` odb-m-${module}` : ''} odb-t-${tone}`}>
      <div className="odb-head">
        {value !== undefined && (
          <span className="odb-figure">
            <span className="odb-value">
              {value}
              {unit !== undefined && <span className="odb-unit">{unit}</span>}
            </span>
          </span>
        )}
        {icon && (
          <span className="odb-tile">
            <i className={`${iconFace} ${icon}`} aria-hidden="true" />
          </span>
        )}
        <span className="odb-names">
          {kicker && <span className="odb-mod">{kicker}</span>}
          <span className="odb-title">{title}</span>
        </span>
      </div>
    </div>
  )
}

/**
 * Bara proportiei: aceeasi cifra, desenata.
 *
 * `.odb-scale` e bara cardurilor din catalog, fara semnele de prag - aici nu
 * exista praguri de unde sa vina. Nu spune nimic in plus fata de procentul de
 * deasupra ei; il face vazut dintr-o privire, de la distanta.
 */
export function BoardScale({ percent }: { percent: number }) {
  const width = Math.max(0, Math.min(100, Math.round(percent)))
  return (
    <span className="odb-scale">
      <span className="odb-scale-fill" style={{ width: `${width}%` }} />
    </span>
  )
}

/**
 * LISTA care se deruleaza in cardul ei.
 *
 * Ecranele de masuratori care se muta pe desenul asta n-au, toate, o cifra si
 * atat: Applications Dashboard arata cate un RAND pentru fiecare aplicatie si
 * pentru fiecare masina urmarita, si lista aia e lunga cat si-a facut-o
 * clientul - de la trei randuri la trei sute.
 *
 * Grila are randuri de inaltime FIXA si cardul se taie la marginea celulei
 * (`.odb-slot > .odb-card { overflow: hidden }`). Fara regula de mai jos, o
 * lista de treizeci de masini ar arata primele cinci si le-ar ASCUNDE pe
 * celelalte, fara ca ceva pe ecran sa spuna ca mai e ceva acolo - adica exact
 * felul de pierdere pe care o mutare de haine n-are voie s-o faca.
 *
 * Deci lista se deruleaza IN cardul ei: `.odb-list` din `BoardLook.css`. Sub
 * 700px grila are randuri dupa continut (`grid-auto-rows: auto`, in
 * `od_dashboard.css`), cardul creste cat ii trebuie si nu apare nicio bara de
 * derulare - pe telefon se deruleaza pagina, ca peste tot.
 *
 * Locul listei e chiar `.odb-body` - trupul cardului - nu un invelis peste el:
 * `.odb-body` e cel care stie sa ia tot ce ramane sub cap fara sa impinga
 * cardul afara din celula.
 */
export function BoardList({ children }: { children: ReactNode }) {
  return <div className="odb-body odb-list">{children}</div>
}

/**
 * Un rand de lista care se DESCHIDE, cu sertarul lui dedesubt.
 *
 * Randul e un buton, nu o legatura: nu duce nicaieri, deschide ceva pe pagina
 * asta (foaia stie deja forma - `button.odb-row`). Bulina din stanga poarta
 * starea randului, fiindca cifra mare a cardului vorbeste despre tot grupul, nu
 * despre randul asta.
 *
 * Sertarul e FRATELE randului, nu copilul lui: un buton nu poate cuprinde
 * altceva care se apasa (contactul are legaturi, textul se selecteaza), si
 * HTML-ul nici nu permite. `aria-expanded` si `aria-controls` leaga cele doua,
 * ca cititorul de ecran sa stie ce a deschis - exact ce spuneau `data-toggle` si
 * `aria-controls` in markupul vechi de bootstrap.
 */
export function BoardLine({
  tone = 'flat',
  label,
  value,
  id,
  open,
  onToggle,
  children,
}: {
  tone?: BoardTone
  label: ReactNode
  /** Cifra din dreapta randului, cand randul are una ("2 devices"). */
  value?: ReactNode
  /** Id-ul sertarului, ca in markupul vechi: `app_12`, `device_7`. */
  id: string
  open: boolean
  onToggle: () => void
  children?: ReactNode
}) {
  return (
    <>
      <button type="button" className="odb-row" aria-expanded={open} aria-controls={id} onClick={onToggle}>
        <span className={`odb-dot odb-c-${tone === 'unknown' ? 'flat' : tone}`} aria-hidden="true" />
        <span className="odb-row-label">{label}</span>
        {value !== undefined && <span className="odb-row-value">{value}</span>}
      </button>
      {open && (
        <div className="odb-fold" id={id}>
          {children}
        </div>
      )}
    </>
  )
}
